import type { SidecarBackend } from '@slideforge/shared';
import { sha256Hex } from '../audio/hash.js';

const SHA_256 = /^[a-f0-9]{64}$/u;

interface CourseDataResponse {
  readonly pointer: string;
}

export interface SidecarAsset {
  readonly audioId: string;
  readonly mimeType: string;
  readonly byteLength: number;
  readonly blob: Blob;
}

/**
 * Same-origin `/api` can be a remote institutional deployment. Automatic
 * narration mirroring is therefore enabled only when the task pane itself is
 * on a loopback origin. A future hosted-mode UI must obtain explicit consent
 * before constructing this client for a non-loopback destination.
 */
export function isLoopbackHostname(hostname: string): boolean {
  const normalized = hostname.toLowerCase().replace(/^\[|\]$/gu, '');
  return normalized === 'localhost' || normalized === '::1' || /^127(?:\.\d{1,3}){3}$/u.test(normalized);
}

export function createLocalSidecarClient(options: {
  readonly hostname?: string;
  readonly baseUrl?: string;
  readonly fetch?: typeof fetch;
} = {}): SlideForgeSidecarClient | undefined {
  const hostname = options.hostname ?? globalThis.location?.hostname ?? '';
  if (!isLoopbackHostname(hostname)) return undefined;
  return new SlideForgeSidecarClient(options);
}

export class SlideForgeSidecarClient implements SidecarBackend {
  readonly available = true;
  readonly #baseUrl: string;
  readonly #fetch: typeof fetch;

  constructor(options: { readonly baseUrl?: string; readonly fetch?: typeof fetch } = {}) {
    this.#baseUrl = (options.baseUrl ?? '/api').replace(/\/$/u, '');
    this.#fetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  }

  async put(courseId: string, payload: string): Promise<string> {
    const response = await this.#fetch(`${this.#baseUrl}/sidecar/course-data`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ courseId, payload }),
    });
    const result = await this.#json<CourseDataResponse>(response);
    if (!SHA_256.test(result.pointer)) throw new Error('The SlideForge service returned an invalid sidecar pointer.');
    return result.pointer;
  }

  async get(pointer: string): Promise<string | undefined> {
    if (!SHA_256.test(pointer)) throw new Error('The course-data sidecar pointer is invalid.');
    const response = await this.#fetch(`${this.#baseUrl}/sidecar/course-data/${pointer}`);
    if (response.status === 404) return undefined;
    await this.#ok(response);
    return response.text();
  }

  async putAsset(asset: SidecarAsset): Promise<void> {
    if (!SHA_256.test(asset.audioId)) throw new Error('Narration audioId must be a SHA-256 digest.');
    if (asset.blob.size !== asset.byteLength) throw new Error('Narration byte length does not match its metadata.');
    if (await sha256Hex(await asset.blob.arrayBuffer()) !== asset.audioId) {
      throw new Error('Narration bytes do not match their audioId.');
    }
    const response = await this.#fetch(`${this.#baseUrl}/sidecar/assets/${asset.audioId}`, {
      method: 'PUT',
      headers: { 'content-type': asset.mimeType },
      body: asset.blob,
    });
    await this.#ok(response);
  }

  async getAsset(audioId: string): Promise<SidecarAsset | undefined> {
    if (!SHA_256.test(audioId)) throw new Error('Narration audioId must be a SHA-256 digest.');
    const response = await this.#fetch(`${this.#baseUrl}/sidecar/assets/${audioId}`);
    if (response.status === 404) return undefined;
    await this.#ok(response);
    const blob = await response.blob();
    if (await sha256Hex(await blob.arrayBuffer()) !== audioId) {
      throw new Error('Narration recovered from the service failed its SHA-256 integrity check.');
    }
    return {
      audioId,
      mimeType: blob.type || 'application/octet-stream',
      byteLength: blob.size,
      blob,
    };
  }

  async #json<T>(response: Response): Promise<T> {
    await this.#ok(response);
    return (await response.json()) as T;
  }

  async #ok(response: Response): Promise<void> {
    if (response.ok) return;
    const detail = await response.text().catch(() => '');
    throw new Error(detail || `SlideForge sidecar request failed with HTTP ${response.status}.`);
  }
}
